import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight, Calendar, Clock, MapPin, AlertCircle } from "lucide-react";
import { useSEO } from "../lib/useSEO";
import { PageDecor } from "../components/decor/PageDecor";
import { formatSwissPhone, isValidSwissPhone, isValidEmail } from "../lib/formHelpers";
import { ChampNaissance } from "../components/ChampNaissance";
import { EmailSuggestion } from "../components/EmailSuggestion";
import { AUDITION, auditionPassee } from "../lib/auditions";
import { useReglages } from "../lib/useReglages";


const API = process.env.REACT_APP_BACKEND_URL;
// Date de l'audition : réglée dans la console (Réglages → Saison) et lue par lib/auditions.js
// — même date partout sur le site. Une fois la date passée, le formulaire se ferme.
const AUDITION_ADDRESS = "1000 Arts, Chemin des Dailles 10E, 1053 Cugy VD";

const CATEGORIES_COMPETITION = [
  "Groupe 7–11 ans (Junior)",
  "Groupe 12–16 ans (Varsity)",
  "Préparation compétition 7–11 ans",
  "Solos et duos IDO",
];

export default function AuditionForm() {
  const { state } = useLocation();
  // La date de l'audition arrive de la console : on attend ce chargement pour l'afficher.
  useReglages();
  const AUDITION_ID = AUDITION.id;
  const AUDITION_DATE_ISO = AUDITION.date_iso;
  const AUDITION_DATE = AUDITION.date_label;
  const passee = auditionPassee();
  // Groupe présélectionné quand on arrive depuis la modale du planning ou une fiche cours.
  const groupeInitial = state?.groupe === "Compagnie" ? "Compagnie" : "Compétition";
  useSEO({
    title: "Inscription à l'audition Compétition & Compagnie — 1000 Arts",
    description: "Inscris-toi à l'audition Compétition & Compagnie de 1000 Arts à Cugy VD. Formulaire simple, réponse rapide de l'école.",
    canonical: "/inscription/audition",
  });

  const [form, setForm] = useState({
    prenom: "", nom: "", dob: "",
    groupe: groupeInitial,          // "Compagnie" | "Compétition"
    categorie: CATEGORIES_COMPETITION[0],
    email: "", tel: "",
    experience: "",
  });
  const [status, setStatus] = useState({ loading: false, ok: null, msg: "" });

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setTel = (e) => setForm({ ...form, tel: formatSwissPhone(e.target.value) });

  const submit = async (e) => {
    e.preventDefault();
    if (!isValidEmail(form.email)) {
      setStatus({ loading: false, ok: false, msg: "Merci de vérifier ton adresse e-mail (ex : prenom.nom@exemple.ch)." });
      return;
    }
    if (!isValidSwissPhone(form.tel)) {
      setStatus({ loading: false, ok: false, msg: "Merci d'entrer un numéro de téléphone suisse valide (ex : +41 79 950 40 31)." });
      return;
    }
    setStatus({ loading: true, ok: null, msg: "" });
    try {
      const res = await fetch(`${API}/api/inscriptions/audition`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          prenom: form.prenom,
          nom: form.nom,
          dob: form.dob,
          groupe: form.groupe,
          categorie: form.groupe === "Compétition" ? form.categorie : "",
          email: form.email,
          tel: form.tel,
          experience: form.experience,
          audition_id: AUDITION_ID,
          audition_date: AUDITION_DATE,
          audition_date_iso: AUDITION_DATE_ISO,
          audition_slot: auditionSlot,
          audition_room: auditionRoom,
          audition_address: AUDITION_ADDRESS,
        }),
      });
      if (!res.ok) throw new Error("Erreur envoi");
      setStatus({ loading: false, ok: true, msg: `Merci ${form.prenom} ! Ton inscription à l'audition est enregistrée.`, slot: auditionSlot, room: auditionRoom });
    } catch (err) {
      setStatus({ loading: false, ok: false, msg: "Une erreur est survenue. Réessaye ou contacte-nous à info1000arts@gmail.com." });
    }
  };

  const isCompagnie = form.groupe === "Compagnie";
  const auditionSlot = isCompagnie ? "12h30 – 15h30" : "14h00 – 17h00";
  const auditionRoom = isCompagnie ? "Salle du haut" : "Salle du bas";

  return (
    <div data-testid="page-audition" className="pt-32 pb-24 relative overflow-hidden mesh-bg">
      <PageDecor />
      <div className="max-w-3xl mx-auto px-6 md:px-12 relative z-10">
      <Link to="/competition" className="btn-ghost mb-6">← Retour à la page Compétition</Link>

      <div className="eyebrow mb-3">Audition</div>
      <h1 className="font-display text-4xl md:text-6xl tracking-tight leading-tight">
        Inscription à l'audition
        {!passee && <><br /><span style={{ color: "var(--primary)" }}>du {AUDITION_DATE.replace(/^\w+\s/, "")}</span></>}
      </h1>

      {/* Infos audition */}
      {passee ? (
        <div className="mt-8 rounded-3xl p-6" data-testid="audition-fermee"
          style={{ background: "#FFF7ED", borderLeft: "4px solid #F97316", color: "#7C2D12" }}>
          <div className="font-display text-2xl mb-2">L'audition de cette saison est passée</div>
          <p className="text-sm leading-relaxed">
            Les inscriptions sont fermées : la prochaine date sera mise en ligne dès que
            possible. Une question ? Écris-nous à <a href="mailto:info1000arts@gmail.com" className="underline">info1000arts@gmail.com</a>{" "}
            ou appelle le 079 950 40 31 — nous te prévenons dès que la nouvelle date est connue.
          </p>
          <Link to="/competition" className="btn-primary mt-5" data-testid="audition-fermee-competition">
            Découvrir les groupes Compétition <ArrowRight size={16} />
          </Link>
        </div>
      ) : (
      <div className="mt-8 rounded-3xl border-2 border-[var(--primary)] p-6 grid md:grid-cols-2 gap-4">
        <div className="flex items-start gap-3">
          <Calendar size={18} style={{ color: "var(--primary)" }} className="mt-1 shrink-0" />
          <div>
            <div className="eyebrow text-[10px]">Date</div>
            <div className="font-medium">{AUDITION_DATE}</div>
          </div>
        </div>
        <div className="flex items-start gap-3">
          <MapPin size={18} style={{ color: "var(--primary)" }} className="mt-1 shrink-0" />
          <div>
            <div className="eyebrow text-[10px]">Lieu</div>
            <div className="text-sm">{AUDITION_ADDRESS}</div>
          </div>
        </div>
      </div>
      )}

      {passee ? null : status.ok ? (
        <div data-testid="audition-success" className="mt-10 card-flat text-center">
          <div className="text-5xl mb-4">🎉</div>
          <h2 className="font-display text-2xl mb-3">Inscription enregistrée !</h2>
          <p className="text-[var(--muted)]">{status.msg}</p>
          <div
            className="mt-6 mx-auto max-w-md p-5 rounded-2xl text-left"
            data-testid="audition-success-rdv"
            style={{ background: "var(--primary-tint)", borderLeft: "4px solid var(--primary)" }}
          >
            <div className="eyebrow text-[10px] mb-2">Ton rendez-vous</div>
            <div className="font-display text-lg leading-tight">{AUDITION_DATE}</div>
            <div className="text-sm mt-1"><strong>Créneau : {status.slot}</strong> · {status.room}</div>
            <div className="text-sm text-[var(--muted)] mt-1">{AUDITION_ADDRESS}</div>
            <div className="text-xs text-[var(--muted)] mt-3 italic">Merci d'arriver 15 minutes en avance.</div>
          </div>
          <p className="text-xs text-[var(--muted)] mt-6 italic">Un e-mail de confirmation vient de t'être envoyé.</p>
        </div>
      ) : (
        <form onSubmit={submit} className="mt-10 space-y-6" data-testid="audition-form">
          <section className="card-flat">
            <div className="font-display text-xl mb-4">Le participant</div>
            <div className="grid md:grid-cols-2 gap-4">
              <Field label="Prénom *" testid="aud-prenom" value={form.prenom} onChange={set("prenom")} required />
              <Field label="Nom *"    testid="aud-nom"    value={form.nom}    onChange={set("nom")}    required />
              <div>
                <label className="field-label">Date de naissance *</label>
                <ChampNaissance testid="aud-dob" value={form.dob} onChange={(v) => setForm((f) => ({ ...f, dob: v }))} required />
              </div>
            </div>
          </section>

          <section className="card-flat">
            <div className="font-display text-xl mb-4">Groupe visé</div>
            <div className="flex gap-3 flex-wrap">
              {["Compétition", "Compagnie"].map((g) => (
                <button
                  key={g}
                  type="button"
                  data-testid={`aud-groupe-${g.toLowerCase()}`}
                  onClick={() => setForm({ ...form, groupe: g })}
                  className={`pill ${form.groupe === g ? "active" : ""}`}
                >
                  {g}
                </button>
              ))}
            </div>

            {form.groupe === "Compétition" && (
              <div className="mt-5" data-testid="aud-categorie-block">
                <label className="field-label">Catégorie souhaitée *</label>
                <select className="input-field" data-testid="aud-categorie" value={form.categorie} onChange={set("categorie")}>
                  {CATEGORIES_COMPETITION.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            )}

            {/* Callout selon groupe */}
            <div
              data-testid="aud-groupe-callout"
              className="mt-5 rounded-2xl p-5 flex items-start gap-3"
              style={{ background: "var(--primary-tint)", borderLeft: "4px solid var(--primary)" }}
            >
              <Clock size={20} style={{ color: "var(--primary)" }} className="mt-0.5 shrink-0" />
              <div className="text-sm text-[var(--text)] leading-relaxed">
                {isCompagnie ? (
                  <>
                    <strong>Créneau : 12h30 – 15h30</strong> · Salle du haut<br />
                    <strong>À préparer :</strong> une chanson d'environ 1 min 30, dans le registre Disney ou comédie musicale.
                  </>
                ) : (
                  <>
                    <strong>Créneau : 14h00 – 17h00</strong> · Salle du bas
                  </>
                )}
                <br />
                <span className="text-xs opacity-80">Merci d'arriver 15 minutes en avance.</span>
              </div>
            </div>
          </section>

          <section className="card-flat">
            <div className="font-display text-xl mb-4">Le responsable légal</div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Field label="E-mail *"    type="email" testid="aud-email" value={form.email} onChange={set("email")} required />
                <EmailSuggestion value={form.email} onFix={(v) => setForm({ ...form, email: v })} testid="aud-email-suggestion" />
              </div>
              <Field label="Téléphone *" type="tel"   testid="aud-tel"   value={form.tel}   onChange={setTel}      required inputMode="tel" placeholder="+41 79 950 40 31" />
            </div>
          </section>

          <section className="card-flat">
            <div className="font-display text-xl mb-4">Expérience préalable <span className="text-sm text-[var(--muted)] font-normal">(optionnel)</span></div>
            <textarea
              data-testid="aud-experience"
              className="input-field min-h-[100px]"
              placeholder="Autres écoles, années de pratique, styles déjà travaillés…"
              value={form.experience}
              onChange={set("experience")}
            />
          </section>

          {status.msg && !status.ok && (
            <div className="p-4 rounded-2xl bg-red-50 border border-red-200 text-red-800 text-sm flex items-start gap-2" data-testid="aud-status">
              <AlertCircle size={16} className="mt-0.5 shrink-0" /> {status.msg}
            </div>
          )}

          <button type="submit" disabled={status.loading} data-testid="aud-submit" className="btn-primary w-full justify-center" style={{ padding: 16 }}>
            {status.loading ? "Envoi…" : "Envoyer mon inscription"} <ArrowRight size={16} />
          </button>
        </form>
      )}
      </div>
    </div>
  );
}

function Field({ label, testid, value, onChange, type = "text", required, min, max, inputMode, placeholder }) {
  return (
    <div>
      <label className="field-label">{label}</label>
      <input
        type={type}
        data-testid={testid}
        className="input-field"
        value={value}
        onChange={onChange}
        required={required}
        min={min}
        max={max}
        inputMode={inputMode}
        placeholder={placeholder}
      />
    </div>
  );
}
